import { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { logout } from '../../slices/authSlice';
import { toast } from 'react-toastify';

import { hasAnyPermission, hasPermission, PERM } from '../../utils/accessPermissions';
import { formatRouteDeniedMessage } from '../../utils/permissionMessages';

const PrivateRoute = () => {
	const { userInfo } = useSelector((state) => state.auth);
	const dispatch = useDispatch();

	useEffect(() => {
		if (userInfo?.forbiddenAccessSys) {
			toast.error('您的账户已被禁止访问系统，请联系管理员');
			dispatch(logout());
		}
	}, [userInfo, dispatch]);

	if (userInfo?.forbiddenAccessSys) {
		return <Navigate to='/loginout' replace />;
	}

	return userInfo ? <Outlet /> : <Navigate to='/loginout' replace />;
};

const PermissionRoute = ({ permission, requireAll = false }) => {
	const { userInfo } = useSelector((state) => state.auth);
	const location = useLocation();
	const permissions = useMemo(() => (Array.isArray(permission) ? permission : [permission]), [permission]);
	const allowed = requireAll ? permissions.every((key) => hasPermission(userInfo, key)) : hasAnyPermission(userInfo, permissions);
	const toastShownRef = useRef(false);

	useEffect(() => {
		if (allowed || toastShownRef.current) return;
		toastShownRef.current = true;
		toast.warning(formatRouteDeniedMessage(permissions), { toastId: `perm-denied-${permissions.join('|')}` });
	}, [allowed, permissions]);

	if (allowed) return <Outlet />;

	return (
		<Navigate
			to='/access-denied'
			replace
			state={{
				requiredPermissions: permissions,
				from: location.pathname,
			}}
		/>
	);
};

const createRoleRoute = (role) => {
	const RoleRoute = () => {
		const { userInfo } = useSelector((state) => state.auth);
		return userInfo?.role === role ? <Outlet /> : <Navigate to='/' replace />;
	};
	RoleRoute.displayName = `${role}Route`;
	return RoleRoute;
};

/** 全库订单查询工作台：经理与管理员共用（路径 /orderlist，不绑角色前缀） */
const ManagerOrAdminRoute = () => {
	const { userInfo } = useSelector((state) => state.auth);
	const role = userInfo?.role;
	return userInfo && (role === 'manager' || role === 'admin') ? <Outlet /> : <Navigate to='/' replace />;
};

const AdminRoute = createRoleRoute('admin');
const ManagerRoute = createRoleRoute('manager');
const FinanceRoute = createRoleRoute('finance');
const BuyerRoute = createRoleRoute('buyer');
const UserRoute = createRoleRoute('user');
const AssistantRoute = createRoleRoute('assistant');
const SaleRoute = createRoleRoute('sale');

const StatisticsRoute = () => {
	const { userInfo } = useSelector((state) => state.auth);
	const allowed = hasPermission(userInfo, PERM.MENU_STATISTICS_VIEW);
	return userInfo && allowed ? <Outlet /> : <Navigate to='/dashboard' replace />;
};

export { PrivateRoute, AdminRoute, PermissionRoute, ManagerRoute, ManagerOrAdminRoute, FinanceRoute, BuyerRoute, UserRoute, AssistantRoute, SaleRoute, StatisticsRoute };
